import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Upload, Wand2, RefreshCw, Download, Coins, AlertCircle, Check, X } from 'lucide-react'
import clsx from 'clsx'
import { generateApi, GenerationModel, GenerationStatus } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

type GenerationType = 'text2img' | 'img2img'

const stylePresets = [
  { id: 'fantasy', name: '판타지', prompt: 'fantasy illustration style, magical atmosphere' },
  { id: 'romance', name: '로맨스', prompt: 'romantic soft lighting, emotional mood' },
  { id: 'martial', name: '무협', prompt: 'chinese martial arts wuxia style' },
  { id: 'modern', name: '현대물', prompt: 'modern urban contemporary setting' },
  { id: 'anime', name: '애니메이션', prompt: 'anime illustration style, vibrant colors' },
  { id: 'realistic', name: '실사', prompt: 'photorealistic, high detail' },
]

const aspectRatios = [
  { id: '1:1', label: '1:1', desc: '정사각형', width: 1024, height: 1024 },
  { id: '3:4', label: '3:4', desc: '세로', width: 768, height: 1024 },
  { id: '4:3', label: '4:3', desc: '가로', width: 1024, height: 768 },
  { id: '9:16', label: '9:16', desc: '표지', width: 576, height: 1024 },
  { id: '16:9', label: '16:9', desc: '와이드', width: 1024, height: 576 },
]

interface GeneratedImage {
  id: string
  imageUrl?: string
  imageData?: string
  status: 'processing' | 'completed' | 'failed'
  progress: number
  error?: string
}

export default function GeneratePage() {
  const navigate = useNavigate()
  const { isAuthenticated, points, updatePoints } = useAuthStore()

  // 로그인 필수 - 비로그인시 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true })
    }
  }, [isAuthenticated, navigate])

  // Form state
  const [type, setType] = useState<GenerationType>('text2img')
  const [prompt, setPrompt] = useState('')
  const [negativePrompt, setNegativePrompt] = useState('blurry, low quality, watermark, text, signature')
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null)
  const [aspectRatio, setAspectRatio] = useState('3:4')
  const [selectedModel, setSelectedModel] = useState<string>('flux-pro')
  const [uploadedImage, setUploadedImage] = useState<File | null>(null)
  const [uploadedImagePreview, setUploadedImagePreview] = useState<string | null>(null)

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([])
  const [error, setError] = useState<string | null>(null)
  const [models, setModels] = useState<GenerationModel[]>([])

  // Polling state
  const [currentJobId, setCurrentJobId] = useState<string | null>(null)
  const [currentAccessKey, setCurrentAccessKey] = useState<string | null>(null)

  // Load models on mount
  useEffect(() => {
    const loadModels = async () => {
      const result = await generateApi.getModels()
      if (result.success && result.data) {
        setModels(result.data)
        // Set default model
        const recommended = result.data.find(m => m.recommended)
        if (recommended) {
          setSelectedModel(recommended.id)
        }
      }
    }
    loadModels()
  }, [])

  // Poll for status
  useEffect(() => {
    if (!currentJobId || !currentAccessKey) return

    const pollStatus = async () => {
      const result = await generateApi.checkStatus(currentJobId, currentAccessKey)

      if (!result.success) {
        setError(result.error?.message || '상태 확인 실패')
        setIsGenerating(false)
        setCurrentJobId(null)
        setCurrentAccessKey(null)
        return
      }

      const status = result.data as GenerationStatus

      // Update generated images with new status
      setGeneratedImages(prev =>
        prev.map(img =>
          img.id === currentJobId
            ? {
                ...img,
                status: status.status === 'Completed' ? 'completed' : status.status === 'Failed' ? 'failed' : 'processing',
                progress: status.progress,
                imageUrl: status.imageUrl,
                imageData: status.imageData,
                error: status.errorMessage,
              }
            : img
        )
      )

      if (status.status === 'Completed') {
        setIsGenerating(false)
        setCurrentJobId(null)
        setCurrentAccessKey(null)

        // Update points
        if (status.actualCost) {
          updatePoints(-status.actualCost)
        }
      } else if (status.status === 'Failed') {
        setIsGenerating(false)
        setCurrentJobId(null)
        setCurrentAccessKey(null)
        setError(status.errorMessage || '생성 실패')
      }
    }

    const interval = setInterval(pollStatus, 2000)
    return () => clearInterval(interval)
  }, [currentJobId, currentAccessKey, updatePoints])

  const handleGenerate = async () => {
    if (!prompt.trim()) return
    if (!isAuthenticated) {
      navigate('/login')
      return
    }

    setError(null)
    setIsGenerating(true)

    // Build full prompt with style
    let fullPrompt = prompt
    if (selectedStyle) {
      const style = stylePresets.find(s => s.id === selectedStyle)
      if (style) {
        fullPrompt = `${prompt}, ${style.prompt}`
      }
    }

    try {
      let result

      if (type === 'text2img') {
        result = await generateApi.text2img({
          prompt: fullPrompt,
          negativePrompt,
          model: selectedModel,
          aspectRatio,
          style: selectedStyle || undefined,
        })
      } else {
        if (!uploadedImage) {
          setError('참고 이미지를 업로드해주세요')
          setIsGenerating(false)
          return
        }

        result = await generateApi.img2img({
          image: uploadedImage,
          prompt: fullPrompt,
          model: selectedModel,
        })
      }

      if (!result.success) {
        setError(result.error?.message || '생성 요청 실패')
        setIsGenerating(false)
        return
      }

      const job = result.data!

      // Add to generated images with processing state
      const newImage: GeneratedImage = {
        id: job.jobId,
        status: 'processing',
        progress: 0,
      }

      setGeneratedImages(prev => [newImage, ...prev])
      setCurrentJobId(job.jobId)
      setCurrentAccessKey(job.accessKey)
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류')
      setIsGenerating(false)
    }
  }

  const handleImageUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setUploadedImage(file)
      const reader = new FileReader()
      reader.onload = () => {
        setUploadedImagePreview(reader.result as string)
      }
      reader.readAsDataURL(file)
    }
  }, [])

  const handleDownload = useCallback((image: GeneratedImage) => {
    if (!image.imageUrl && !image.imageData) return

    const link = document.createElement('a')
    if (image.imageUrl) {
      link.href = image.imageUrl
    } else if (image.imageData) {
      link.href = `data:image/png;base64,${image.imageData}`
    }
    link.download = `generated-${image.id}.png`
    link.click()
  }, [])

  const handleUseInEditor = useCallback((image: GeneratedImage) => {
    // TODO: Navigate to editor with image
    const imageUrl = image.imageUrl || (image.imageData ? `data:image/png;base64,${image.imageData}` : null)
    if (imageUrl) {
      // Store in sessionStorage for editor to pick up
      sessionStorage.setItem('pendingEditorImage', JSON.stringify({ url: imageUrl, name: 'AI Generated' }))
      navigate('/editor')
    }
  }, [navigate])

  const currentModel = models.find(m => m.id === selectedModel)
  const estimatedCost = currentModel?.cost || (type === 'text2img' ? 20 : 30)
  const hasEnoughPoints = points >= estimatedCost

  return (
    <div className="min-h-[calc(100vh-56px)] p-4 lg:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-accent-gold" />
            AI 이미지 생성
          </h1>
          <p className="text-text-secondary mt-1">
            텍스트 프롬프트로 원하는 이미지를 생성하세요
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-accent-error/10 border border-accent-error/30 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-accent-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-accent-error font-medium">오류가 발생했습니다</p>
              <p className="text-sm text-text-secondary mt-1">{error}</p>
            </div>
            <button onClick={() => setError(null)} className="text-text-tertiary hover:text-text-primary">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left - Input */}
          <div className="space-y-6">
            {/* Type Tabs */}
            <div className="flex gap-2 p-1 bg-bg-secondary rounded-lg">
              <button
                onClick={() => setType('text2img')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  type === 'text2img'
                    ? 'bg-accent-gold text-white'
                    : 'text-text-secondary hover:text-text-primary'
                )}
              >
                <Wand2 className="w-4 h-4" />
                Text to Image
              </button>
              <button
                onClick={() => setType('img2img')}
                className={clsx(
                  'flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
                  type === 'img2img'
                    ? 'bg-accent-gold text-white'
                    : 'text-text-secondary hover:text-text-primary'
                )}
              >
                <RefreshCw className="w-4 h-4" />
                Image to Image
              </button>
            </div>

            {/* Image Upload (for img2img) */}
            {type === 'img2img' && (
              <div className="card p-4">
                <label className="block cursor-pointer">
                  <span className="text-sm font-medium mb-2 block">참고 이미지</span>
                  <div className="border-2 border-dashed border-border-default rounded-lg p-8 text-center hover:border-accent-gold transition-colors">
                    {uploadedImagePreview ? (
                      <div className="relative">
                        <img
                          src={uploadedImagePreview}
                          alt="Uploaded"
                          className="max-h-48 mx-auto rounded-lg"
                        />
                        <button
                          onClick={(e) => {
                            e.preventDefault()
                            setUploadedImage(null)
                            setUploadedImagePreview(null)
                          }}
                          className="absolute top-2 right-2 p-1 bg-bg-primary/80 rounded-full hover:bg-bg-primary"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <Upload className="w-8 h-8 mx-auto text-text-tertiary mb-2" />
                        <p className="text-text-secondary">클릭하거나 이미지를 드래그하세요</p>
                        <p className="text-xs text-text-tertiary mt-1">JPG, PNG, WebP (최대 20MB)</p>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </div>
                </label>
              </div>
            )}

            {/* Prompt Input */}
            <div className="card p-4 space-y-4">
              <div>
                <label className="text-sm font-medium mb-2 block">프롬프트</label>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder="생성할 이미지를 설명해주세요... (예: A beautiful fantasy castle with golden towers at sunset)"
                  rows={4}
                  className="input resize-none"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block text-text-secondary">
                  네거티브 프롬프트 (선택)
                </label>
                <textarea
                  value={negativePrompt}
                  onChange={(e) => setNegativePrompt(e.target.value)}
                  placeholder="제외할 요소... (예: blurry, low quality, text)"
                  rows={2}
                  className="input resize-none text-sm"
                />
              </div>
            </div>

            {/* Model Selection */}
            {models.length > 0 && (
              <div className="card p-4">
                <label className="text-sm font-medium mb-3 block">모델</label>
                <div className="grid grid-cols-2 gap-2">
                  {models.map((model) => (
                    <button
                      key={model.id}
                      onClick={() => setSelectedModel(model.id)}
                      className={clsx(
                        'p-3 rounded-lg text-left transition-colors border',
                        selectedModel === model.id
                          ? 'border-accent-gold bg-accent-gold/10'
                          : 'border-border-default hover:border-accent-gold/50'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{model.name}</span>
                        {model.recommended && (
                          <span className="text-xs px-2 py-0.5 bg-accent-gold/20 text-accent-gold rounded">추천</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Coins className="w-3 h-3 text-accent-gold" />
                        <span className="text-xs text-text-secondary">{model.cost}P</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Style Presets */}
            <div className="card p-4">
              <label className="text-sm font-medium mb-3 block">스타일</label>
              <div className="grid grid-cols-3 gap-2">
                {stylePresets.map((style) => (
                  <button
                    key={style.id}
                    onClick={() => setSelectedStyle(selectedStyle === style.id ? null : style.id)}
                    className={clsx(
                      'px-3 py-2 rounded-lg text-sm transition-colors',
                      selectedStyle === style.id
                        ? 'bg-accent-gold text-white'
                        : 'bg-bg-elevated text-text-secondary hover:text-text-primary'
                    )}
                  >
                    {style.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Aspect Ratio */}
            <div className="card p-4">
              <label className="text-sm font-medium mb-3 block">비율</label>
              <div className="flex gap-2">
                {aspectRatios.map((ratio) => (
                  <button
                    key={ratio.id}
                    onClick={() => setAspectRatio(ratio.id)}
                    className={clsx(
                      'flex-1 px-3 py-2 rounded-lg text-sm transition-colors',
                      aspectRatio === ratio.id
                        ? 'bg-accent-gold text-white'
                        : 'bg-bg-elevated text-text-secondary hover:text-text-primary'
                    )}
                  >
                    <div className="font-medium">{ratio.label}</div>
                    <div className="text-xs opacity-70">{ratio.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <button
              onClick={handleGenerate}
              disabled={!prompt.trim() || isGenerating || !hasEnoughPoints}
              className="btn btn-primary w-full py-3 text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                  생성 중...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  이미지 생성하기
                  <span className="ml-2 flex items-center text-sm opacity-80">
                    <Coins className="w-4 h-4 mr-1" />
                    {estimatedCost}P
                  </span>
                </>
              )}
            </button>

            {/* Points Warning */}
            {!hasEnoughPoints && (
              <p className="text-center text-sm text-accent-error">
                포인트가 부족합니다. (보유: {points}P)
              </p>
            )}
          </div>

          {/* Right - Results */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">생성 결과</h2>

            {generatedImages.length > 0 ? (
              <div className="grid grid-cols-2 gap-4">
                {generatedImages.map((image) => (
                  <div key={image.id} className="card-elevated group relative overflow-hidden rounded-lg">
                    {image.status === 'processing' ? (
                      <div className="aspect-[3/4] bg-bg-elevated flex flex-col items-center justify-center">
                        <RefreshCw className="w-8 h-8 text-accent-gold animate-spin mb-3" />
                        <p className="text-sm text-text-secondary">생성 중...</p>
                        <div className="w-24 h-1 bg-bg-tertiary rounded-full mt-2 overflow-hidden">
                          <div
                            className="h-full bg-accent-gold transition-all duration-300"
                            style={{ width: `${image.progress}%` }}
                          />
                        </div>
                      </div>
                    ) : image.status === 'failed' ? (
                      <div className="aspect-[3/4] bg-bg-elevated flex flex-col items-center justify-center text-accent-error">
                        <AlertCircle className="w-8 h-8 mb-2" />
                        <p className="text-sm">생성 실패</p>
                        <p className="text-xs text-text-tertiary mt-1">{image.error}</p>
                      </div>
                    ) : (
                      <>
                        <img
                          src={image.imageUrl || `data:image/png;base64,${image.imageData}`}
                          alt="Generated"
                          className="aspect-[3/4] w-full object-cover"
                        />
                        {/* Success indicator */}
                        <div className="absolute top-2 right-2 p-1 bg-accent-success/90 rounded-full">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                        {/* Hover overlay */}
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleUseInEditor(image)}
                            className="btn btn-primary text-sm"
                          >
                            사용하기
                          </button>
                          <button
                            onClick={() => handleDownload(image)}
                            className="p-2 bg-bg-tertiary rounded-lg hover:bg-bg-elevated transition-colors"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="card p-12 text-center">
                <Sparkles className="w-12 h-12 mx-auto text-text-tertiary mb-4" />
                <p className="text-text-secondary">
                  프롬프트를 입력하고 생성 버튼을 눌러주세요
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
